'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslations, useLocale } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/browser';
import { useTenantId } from '@/lib/tenant/client';
import { trackCompleteRegistration } from '@/lib/tracking/events';
import { Button } from '@/components/storefront/ui/button';
import { Field, Input } from '@/components/storefront/ui/input';
import { useState } from 'react';
import { Eye, EyeSlash, EnvelopeSimple, LockSimple, SpinnerGap } from '@phosphor-icons/react';
import { Link } from '@/i18n/navigation';
import { OAuthButtons } from './OAuthButtons';

type SignUpValues = {
  email: string;
  password: string;
  confirmPassword: string;
};

export function SignUpForm() {
  const t = useTranslations('auth');
  const router = useRouter();
  const locale = useLocale();
  const tenantId = useTenantId();
  const searchParams = useSearchParams();
  const next = searchParams.get('next');
  const [serverError, setServerError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const signUpSchema = z
    .object({
      email: z.string().email(),
      password: z.string().min(8),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('passwordsDoNotMatch'),
      path: ['confirmPassword'],
    });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { email: '', password: '', confirmPassword: '' },
  });

  const onSubmit = async (values: SignUpValues) => {
    setServerError(null);

    const supabase = createClient();
    const { error } = await supabase.auth.signUp({
      email: values.email,
      password: values.password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback?locale=${locale}${next ? `&next=${encodeURIComponent(next)}` : ''}`,
        data: { tenant_id: tenantId },
      },
    });

    if (error) {
      setServerError(error.message);
      return;
    }

    trackCompleteRegistration();
    router.push(`/${locale}/verify-email?email=${encodeURIComponent(values.email)}`);
  };

  return (
    <div className="w-full max-w-md space-y-6">
      <OAuthButtons next={next} />

      <div className="relative flex items-center">
        <div className="flex-1 border-t border-border" />
        <span className="px-3 text-xs text-muted-foreground uppercase tracking-wider">
          {t('orContinueWith')}
        </span>
        <div className="flex-1 border-t border-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <Field label={t('email')} error={errors.email?.message}>
          {(id, describedBy) => (
            <Input
              id={id}
              type="email"
              autoComplete="email"
              size="lg"
              prefix={<EnvelopeSimple size={18} />}
              invalid={!!errors.email}
              aria-describedby={describedBy}
              placeholder="you@example.com"
              {...register('email')}
            />
          )}
        </Field>

        <Field
          label={t('password')}
          hint={t('passwordMinHint')}
          error={errors.password?.message}
        >
          {(id, describedBy) => (
            <Input
              id={id}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              size="lg"
              prefix={<LockSimple size={18} />}
              suffix={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="pointer-events-auto p-1 -m-1 hover:text-foreground transition-colors"
                  aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              }
              invalid={!!errors.password}
              aria-describedby={describedBy}
              {...register('password')}
            />
          )}
        </Field>

        <Field label={t('confirmPassword')} error={errors.confirmPassword?.message}>
          {(id, describedBy) => (
            <Input
              id={id}
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              size="lg"
              prefix={<LockSimple size={18} />}
              suffix={
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="pointer-events-auto p-1 -m-1 hover:text-foreground transition-colors"
                  aria-label={showConfirm ? t('hidePassword') : t('showPassword')}
                >
                  {showConfirm ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              }
              invalid={!!errors.confirmPassword}
              aria-describedby={describedBy}
              {...register('confirmPassword')}
            />
          )}
        </Field>

        {serverError && (
          <p className="text-sm text-danger" role="alert">{serverError}</p>
        )}

        <Button
          type="submit"
          variant="solid"
          color="primary"
          size="lg"
          fullWidth
          disabled={isSubmitting}
          leftIcon={isSubmitting ? <SpinnerGap className="animate-spin" /> : null}
        >
          {t('signup')}
        </Button>

        <p className="text-xs text-muted-foreground text-center">{t('termsNotice')}</p>
      </form>

      <p className="text-center text-sm text-muted-foreground">
        {t('hasAccount')}{' '}
        <Link href="/login" className="text-primary hover:underline font-medium">
          {t('login')}
        </Link>
      </p>
    </div>
  );
}
